import { useNavigate, useParams } from "react-router-dom";

import toast from "react-hot-toast";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Form, Formik } from "formik";
import * as Yup from "yup";
import { ItemStatus } from "../../../components/Utils";
import { PMField } from "../../../components/utils/PMField";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { useUserHasAccess } from "../../../routes/roleOnly";
import {
  deleteAbsence,
  updateAbsence,
  exportAbsencePDF,
} from "../../../api/absence/absenece";
import { PMNotice } from "../../../components/utils/notifications";
import { CheckBadgeIcon, TrashIcon, DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../../components/utils/PMDialog";
import { PMButton } from "../../../components/utils/PMButton";
import PMSwitch from "../../../components/utils/PMSwitch";
import ProConfirmation from "../../../components/utils/ProConfirmation";
import { AbsenceForm } from "../../../components/dashboard/absence/absence_form";

export const EditAbsence = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const isAdmin = useUserHasAccess({ role: "absence", action: "approve" });

  // The shared AbsenceForm owns the form state; the page keeps a copy of the
  // loaded/saved absence so the header chrome (PDF / approve / delete) stays in sync.
  const [absence, setAbsence] = useState(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [approveErrors, setApproveErrors] = useState([]);

  // userType from searchParams (kept for the page title)
  const searchParams = new URLSearchParams(location.search);
  const userType = searchParams.get("userType") || "";

  const handleAbsenceSaved = (saved) => {
    const wasNew = !id;
    setAbsence(saved);
    if (wasNew && saved?.id) {
      navigate(`/dashboard/absences/${saved.id}`);
    }
  };

  const canApprove = () => {
    if (isAdmin && !absence?.accepted) {
      return true;
    }
    return false;
  };

  const handleExportPDF = async () => {
    if (isExportingPDF) return;

    if (!absence?.id) {
      toast.error(t("absence.edit.pdf_must_save"));
      return;
    }

    if (!absence?.accepted && absence?.status !== "approved") {
      toast.error(t("absence.edit.pdf_must_approve"));
      return;
    }

    setIsExportingPDF(true);
    try {
      const result = await exportAbsencePDF(absence.id);

      if (result.success && result.data) {
        let blob;
        if (result.data instanceof Blob) {
          blob = result.data;
        } else {
          blob = new Blob([result.data], { type: 'application/pdf' });
        }

        if (blob.size < 100) {
          throw new Error(t("absence.edit.pdf_invalid"));
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `fravaer_${absence.id}_${absence.date_from}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success(t("absence.edit.pdf_exported"));
      } else {
        const errorMsg = result.errors?.join(", ") || t("absence.edit.pdf_failed");
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error(error.message || t("absence.edit.pdf_failed"));
    } finally {
      setIsExportingPDF(false);
    }
  };

  const submitApproval = async (values, helpers) => {
    try {
      setApproveErrors([]);
      const payload = {
        ...values,
        user_id: absence?.user?.id || absence?.user_id,
      };
      const result = await updateAbsence(payload);

      if (
        result.success === false ||
        (result.errors && Array.isArray(result.errors) && result.errors.length > 0)
      ) {
        setApproveErrors(result.errors || [t("absence.edit.unknown_save_error")]);
        helpers.setSubmitting(false);
        return;
      }

      const data = result.data;
      if (!data || (data.errors && Array.isArray(data.errors) && data.errors.length > 0)) {
        setApproveErrors((data && data.errors) || [t("absence.edit.unknown_save_error")]);
        helpers.setSubmitting(false);
        return;
      }

      setAbsence(data);
      helpers.setStatus({ success: true });
      helpers.setSubmitting(false);
      toast.success(t("absence.edit.saved"));
      setApproveErrors([]);
      setApproveDialogOpen(false);
    } catch (err) {
      helpers.setSubmitting(false);
      setApproveErrors([t("absence.edit.unknown_save_error")]);
    }
  };

  const handleDeleteAbsence = () => {
    setDeleteConfirmationOpen(false);
    if (!absence?.id) return;
    deleteAbsence(absence.id)
      .then(() => {
        navigate("/dashboard/absences/overview");
      })
      .catch(() => {
        toast.error(t("absence.edit.delete_failed"));
      });
  };

  const headerActions = [
    {
      component: null,
      label: isExportingPDF ? t("absence.edit.exporting") : t("absence.edit.export_pdf"),
      description: "",
      path: null,
      action: handleExportPDF,
      icon: DocumentArrowDownIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: !absence?.id || (!absence?.accepted && absence?.status !== "approved"),
      menu: false,
      disabled: isExportingPDF,
    },
    {
      component: null,
      label: t("absence.edit.approve"),
      description: "",
      path: null,
      action: () => setApproveDialogOpen(true),
      icon: CheckBadgeIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: !canApprove(),
      menu: false,
    },
    {
      component: null,
      label: t("absence.edit.delete"),
      description: "",
      path: null,
      action: () => setDeleteConfirmationOpen(true),
      icon: TrashIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: !isAdmin || !absence?.id,
      menu: true,
    },
  ];

  const headerMeta = [
    {
      label: "wd",
      component: <ItemStatus item={absence} />,
      path: null,
      action: null,
      icon: CheckBadgeIcon,
      textColor: "text-teal-700",
      hidden: false,
    },
    {
      label: t("absence.edit.approved_by", { name: absence?.approved_by?.displayTitle }),
      path: null,
      action: null,
      icon: CheckBadgeIcon,
      textColor: "text-teal-700",
      hidden: !absence?.accepted,
    },
  ];

  return (
    <>
      <DashboardHeader
        subtitle=""
        meta={headerMeta}
        actions={headerActions}
        title={
          `${absence?.id ? t("absence.edit.title_edit") : t("absence.edit.title_new")}${userType ? ` – ${userType.toUpperCase()}` : ""}`
        }
      >
        <div className="mx-auto mt-8 w-full max-w-3xl px-3 sm:px-4">
          <AbsenceForm
            absenceId={id || null}
            onLoaded={setAbsence}
            onChange={handleAbsenceSaved}
            onClose={() => navigate("/dashboard/absences/overview")}
          />
        </div>

        {isAdmin && (
          <PMDialog
            title={t("absence.edit.approve_title")}
            open={approveDialogOpen}
            onClose={() => setApproveDialogOpen(false)}
          >
            <Formik
              enableReinitialize={false}
              initialValues={{
                id: absence?.id,
                accepted: absence?.accepted,
                denied: absence?.denied,
                approval_note: absence?.approval_note,
              }}
              validationSchema={Yup.object({
                description: Yup.string(),
              })}
              onSubmit={submitApproval}
            >
              {(formik) => (
                <Form>
                  {/* Error Display */}
                  {approveErrors && approveErrors.length > 0 && (
                    <div className="mb-6">
                      <PMNotice
                        severity="error"
                        title={t("absence.edit.error_title")}
                        description={
                          <div className="mt-2">
                            <ul className="list-disc list-inside space-y-1">
                              {approveErrors.map((error, index) => (
                                <li key={index} className="text-sm">
                                  {error}
                                </li>
                              ))}
                            </ul>
                          </div>
                        }
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 sm:grid-cols-2 gap-6">
                    <div className="col-span-1 sm:col-span-1">
                      <PMSwitch
                        label={t("absence.edit.approved_label")}
                        name="accepted"
                        {...formik}
                        onChange={(e) => {
                          formik.setFieldValue("denied", false);
                          formik.handleChange(e);
                        }}
                      />
                    </div>

                    <div className="col-span-1 sm:col-span-1">
                      <PMSwitch
                        label={t("absence.edit.denied_label")}
                        name="denied"
                        {...formik}
                        onChange={(e) => {
                          formik.setFieldValue("accepted", false);
                          formik.handleChange(e);
                        }}
                      />
                    </div>

                    <div className="col-span-2 sm:col-span-2">
                      <PMField
                        label={t("absence.edit.note")}
                        name="approval_note"
                        multiline={true}
                        rows={2}
                        {...formik}
                      />
                    </div>

                    <div className="col-span-2 sm:col-span-2 flex justify-between">
                      <PMButton
                        type="button"
                        onClick={() => setApproveDialogOpen(false)}
                        className="bg-slate-400 hover:bg-slate-200 hover-bg- max-w-1/2"
                      >
                        {t("absence.edit.cancel")}
                      </PMButton>
                      <PMButton className="ml-10 max-w-1/2" type="submit">
                        {t("absence.edit.save")}
                      </PMButton>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </PMDialog>
        )}
      </DashboardHeader>

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={t("absence.edit.delete_confirm")}
        action="delete"
        module="order_item"
        yesCallback={handleDeleteAbsence}
        cancelCallback={() => setDeleteConfirmationOpen(false)}
      />
    </>
  );
};
